import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { animeList, categories } from '@/mocks/anime';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import AnimeCard from '@/pages/home/components/AnimeCard';

export default function AnimeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const animeId = parseInt(id || '0', 10);
  const anime = animeList.find((a) => a.id === animeId);
  const category = anime ? categories.find((c) => c.id === anime.category) : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const relatedAnime = anime
    ? animeList.filter((a) => a.category === anime.category && a.id !== anime.id).slice(0, 4)
    : [];

  if (!anime) {
    return (
      <>
        <SEOHead
          title="番剧未找到 - 91动漫"
          description="抱歉，您访问的番剧页面不存在。请浏览91动漫的其他番剧。"
          keywords="91动漫,动漫番剧"
        />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">番剧未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium text-sm whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${anime.title} - 91动漫高清在线观看`,
        description: `${anime.title}：${anime.description.substring(0, 120)}`,
        url: `https://www.91anime.com/anime/${anime.id}`,
        image: anime.cover,
        datePublished: `${anime.year}-01-01`,
        author: {
          '@type': 'Organization',
          name: '91动漫',
        },
        publisher: {
          '@type': 'Organization',
          name: '91动漫',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${anime.title} - 91动漫高清在线观看 | ${anime.tags.join(' ')}`}
        description={`${anime.title}：${anime.description.substring(0, 120)} - 91动漫正版高清在线观看平台，${anime.episodes}，评分${anime.rating}，${anime.views}次观看。`}
        keywords={`91动漫,${anime.title},${anime.title}在线观看,${anime.tags.join(',')},动漫在线观看,91动漫番剧`}
        canonicalUrl={`https://www.91anime.com/anime/${anime.id}`}
        ogType="article"
        ogImage={anime.cover}
        structuredData={structuredData}
      />

      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: category ? category.name : '分类', path: category ? `/category/${category.slug}` : '/category/rexue' },
            { label: anime.title },
          ]}
        />

        <article className="w-full py-8 md:py-12 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0 w-full lg:w-[300px]">
                <div className="aspect-[3/4] rounded-xl overflow-hidden border border-background-200/70">
                  <img
                    src={anime.cover}
                    alt={anime.title}
                    title={`${anime.title} - 91动漫高清在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  {anime.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-100 text-accent-700 text-sm font-semibold">
                    <i className="ri-star-fill"></i> {anime.rating}
                  </div>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>{anime.views}次观看
                  </span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-calendar-line mr-1"></i>{anime.year}
                  </span>
                  <span className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                    anime.status === '已完结' ? 'bg-secondary-100 text-secondary-700' : 'bg-primary-100 text-primary-700'
                  }`}>
                    {anime.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {anime.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-900 mb-2">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{anime.description}</p>
                </div>

                <div className="flex items-center gap-3 flex-col sm:flex-row">
                  <button className="w-full sm:w-auto px-8 py-3 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line mr-1.5"></i>开始观看
                  </button>
                  <button className="w-full sm:w-auto px-8 py-3 rounded-lg bg-background-100 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-all whitespace-nowrap cursor-pointer border border-background-200/70">
                    <i className="ri-heart-line mr-1.5"></i>加入追番
                  </button>
                </div>
              </div>
            </div>

            {relatedAnime.length > 0 && (
              <section className="mt-14 pt-10 border-t border-background-200/70">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-heading font-bold text-foreground-950">同类推荐</h2>
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                    >
                      查看更多 <i className="ri-arrow-right-line"></i>
                    </Link>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {relatedAnime.map((a) => (
                    <AnimeCard key={a.id} {...a} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}